import moment from "moment";
import {Fragment, useCallback} from "react";
import {useSelector} from "react-redux";
import AnimalReferenceDocumentModalGrid from "../components/grids/animal-reference-document/AnimalReferenceDocumentModalGrid";
import {ParamTypes} from "../constans/paramTypes";
import animalsDB from "../database/animalsDB";
import groupsDB from "../database/groupsDB";
import {getFarmID} from "../selectors/farmSelector";
import {useSQLChartFormatters} from "./useSQLChartFormatters";

export function useDetailsFormatter() {
    const farm = useSelector(getFarmID);

    const dateFormatter = useCallback((value) => {
        if (!value) return "";
        if (!Array.isArray(value)) {
            if (typeof value === "object") return "";
            return moment(value).format("L");
        }
        return value.map((row) => moment(row).format("L")).join(", ");
    }, []);

    const animalFormatter = useCallback(
        (value) => {
            if (Array.isArray(value)) {
                return value.map((row, index) => {
                    const animal = animalsDB.getAnimalById(row, {
                        joinEvents: false,
                        findDeleted: true,
                    });
                    if (!animal) return null;
                    return (
                        <Fragment key={index}>
                            <AnimalReferenceDocumentModalGrid
                                object={animal}
                                key={row}
                            />
                            {index < value.length - 1 && <span>, </span>}
                        </Fragment>
                    );
                });
            }
            const object =
                animalsDB.getAnimalById(value, {
                    joinEvents: false,
                    findDeleted: true,
                }) || groupsDB.getGroupByAnmGrp(value, farm);
            if (!object) return "";
            return <AnimalReferenceDocumentModalGrid object={object} />;
        },
        [farm]
    );

    const numberFormatter = useCallback((value) => {
        if (value === null || value === undefined || typeof value !== "number")
            return "";
        return value.toFixed(2);
    }, []);

    const getParameterFormatter = useSQLChartFormatters("Table");

    return useCallback(
        (name, parameter, key) => {
            console.log(name, parameter, key);
            if (parameter?.parameter === "MATH") return null;
            if (name.endsWith("_Date")) return dateFormatter;
            if (name.endsWith("_PlcmntID"))
                return getParameterFormatter(ParamTypes.LOCATION);
            if (name.endsWith("_Dictionary"))
                return getParameterFormatter(ParamTypes.DICTIONARY);
            if (name.endsWith("_Day"))
                return getParameterFormatter(ParamTypes.WEEK_DAY);
            if (name.endsWith("_User"))
                return getParameterFormatter(ParamTypes.USER);
            const amountFields = [
                "GroupDay",
                "Month",
                "Week",
                "Year",
                "CycleDay",
                "CycleWeek",
                "Cycle",
            ];
            if (amountFields.some((field) => name.endsWith(`_${field}`)))
                return getParameterFormatter(ParamTypes.AMOUNT, {precision: 0});
            if (
                [
                    "COUNT_ANIMAL",
                    "COUNT_ANIMAL_DISTINCT",
                    "COUNT_VALUES",
                    "COUNT_VALUES_DISTINCT",
                ].includes(parameter?.type)
            ) {
                return getParameterFormatter(key?.type);
            }
            switch (name) {
                case "AnmID":
                case "inseminationBoar":
                    return animalFormatter;
                case "inseminationDate":
                case "usgDate":
                case "noPregnancy":
                    return dateFormatter;
                case "usgResult":
                    return getParameterFormatter(ParamTypes.RESULT);
                case "fallPigletsAge":
                    return numberFormatter;
                case "weighting":
                    return getParameterFormatter(ParamTypes.WEIGHT);
                case "plcmntID":
                    return getParameterFormatter(ParamTypes.LOCATION);
                case "race":
                    return getParameterFormatter(ParamTypes.RACE);
                case "raceCode":
                    return getParameterFormatter(ParamTypes.RACE_CODE);
                default:
                    // if (name.endsWith("_Date")) return dateFormatter;
                    // if (name.endsWith("_PlcmntID")) return getParameterFormatter(ParamTypes.LOCATION);
                    // if (name.endsWith("_Dictionary")) return getParameterFormatter(ParamTypes.DICTIONARY);
                    return null;
            }
        },
        [dateFormatter, animalFormatter, numberFormatter, getParameterFormatter]
    );
}
